import { Article, Download, PermContactCalendar, PictureAsPdf, RepeatOn } from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import { MemberExportFormatEnum } from 'src/shared/models/enums/memberExportFormat.enum';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { Box, Stack } from '@mui/system';
import { Button, Alert, AlertTitle, Divider } from '@mui/material';
import { MemberAdvancedSearchExportFormat } from './memberAdvancedSearchExportFormat';
import { exportProperties, MemberAdvancedSearchExportFilter, PROFILE_GROUP_PREFIX } from './memberAdvancedSearchExportFilter';
import { MemberExportFilter } from '../pages/memberAdvancedSearchPage';

export interface MemberExportTypeItem {
  label: string;
  key: MemberExportFormatEnum;
  icon: JSX.Element;
}

type GetMemberExportTypesProps = {
  otherContactMode: boolean;
};

// Typing for form values, key: value
type ExportFilterValues = Map<string, string | number | boolean | undefined>;

export const GetMemberExportTypes = (props: GetMemberExportTypesProps): MemberExportTypeItem[] => {
  const { otherContactMode } = props;
  const { t } = useTranslation(['member']);
  const { currentUser } = useGlobals();
  const memberExportTypes: MemberExportTypeItem[] = [];
  const otherLabel = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.OtherLabel, t('member:otherContact.pageHeader'));
  const displayLabel = otherContactMode ? otherLabel : t('member:editMember.memberLabel');

  memberExportTypes.push({
    label: t('member:exportModal.exportFormat.CSV'),
    key: MemberExportFormatEnum.CSV,
    icon: <Article className='icon' />,
  });

  memberExportTypes.push({
    label: t('member:exportModal.exportFormat.AddressLabels'),
    key: MemberExportFormatEnum.AddressLabels,
    icon: <PictureAsPdf className='icon' />,
  });

  memberExportTypes.push({
    label: otherContactMode ? displayLabel : t('member:exportModal.exportFormat.MemberList'),
    key: MemberExportFormatEnum.MemberList,
    icon: otherContactMode ? <RepeatOn className='icon' /> : <PermContactCalendar className='icon' />,
  });

  memberExportTypes.push({
    label: otherContactMode ? displayLabel : t('member:exportModal.exportFormat.BirthdayList'),
    key: MemberExportFormatEnum.BirthdayList,
    icon: otherContactMode ? <RepeatOn className='icon' /> : <PermContactCalendar className='icon' />,
  });

  return memberExportTypes;
};

type MemberAdvancedSearchExportProps = {
  otherContactMode: boolean;
  exportCallback: (format: string, filter: MemberExportFilter) => void;
};

export const MemberAdvancedSearchExport = (props: MemberAdvancedSearchExportProps) => {
  const { otherContactMode, exportCallback } = props;
  const { t } = useTranslation(['member', 'common']);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [searchFilter, setSearchFilter] = useState<ExportFilterValues>();

  const exportTypes: MemberExportTypeItem[] = GetMemberExportTypes({ otherContactMode });

  function getMemberExportTypeByKey(key: MemberExportFormatEnum): MemberExportTypeItem | undefined {
    return exportTypes.find((exportType) => exportType.key === key);
  }

  const [format, setFormat] = useState<MemberExportFormatEnum>(exportTypes[0].key);
  const [formatSelected, setFormatSelected] = useState<boolean>(false);

  const handleExport = () => {
    // Take the array of keys in exportProperties and find the ones with truthy values and filter out an array of keys
    const selectedExportProperties: Array<string> = searchFilter
      ? exportProperties
          .map((key) => [key, searchFilter.get(key)])
          .filter((item) => item[1] !== undefined)
          .map((item) => item[0] as string)
      : [];

    // Handled separatly, add to the same array of keys if truthy
    if (searchFilter?.get('familyHeadMemberNr')) {
      selectedExportProperties.push('familyHeadMemberNr');
    }

    // Dynamiska profilkolumner (profileGroup_{id}) – ta med de valda.
    if (searchFilter) {
      searchFilter.forEach((value, key) => {
        if (value && key.startsWith(PROFILE_GROUP_PREFIX)) {
          selectedExportProperties.push(key);
        }
      });
    }

    // Map to MemberExportFilter
    const exportFilter: MemberExportFilter = {
      sortField: (searchFilter?.get('sortField') as string) || 'lastname',
      sortOrder: (searchFilter?.get('orderDirection') as string) || 'asc',
      exportProperties: selectedExportProperties,
    };

    // Call parent with format and exportFilter
    exportCallback(format, exportFilter);
    setShowModal(false);
  };

  return (
    <>
      <Button variant='outlined' disableElevation size='large' endIcon={<Download fontSize='small' />} onClick={() => setShowModal(true)}>
        {t('common:button.export')}
      </Button>
      <GenericDialog
        open={showModal}
        onClose={() => setShowModal(false)}
        onSave={() => handleExport()}
        dialogTitle={t('member:exportModal.title')}
        dialogSubTitle={formatSelected ? <>{getMemberExportTypeByKey(format)?.label}</> : undefined}
        maxWidth='md'
        padding={0}
        dialogContent={
          <Box>
            <Alert severity='info'>
              <AlertTitle>{t('member:exportModal.alertTitle')}</AlertTitle>
              {t('member:exportModal.alertMessage')}
            </Alert>
            <Divider sx={{ mb: 3 }} />
            {!formatSelected && (
              <MemberAdvancedSearchExportFormat memberExportTypes={exportTypes} selectedFormat={format} callback={(x) => setFormat(x)} />
            )}
            {formatSelected && <MemberAdvancedSearchExportFilter updateState={(x) => setSearchFilter(x)} />}
          </Box>
        }
        dialogActions={
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Button variant='outlined' onClick={() => setShowModal(false)}>
              {t('common:button.cancel')}
            </Button>
            {!formatSelected && format === MemberExportFormatEnum.CSV && (
              <Button variant='contained' color='primary' onClick={() => setFormatSelected(true)} disableElevation>
                {t('common:button.next')}
              </Button>
            )}
            {(formatSelected || format !== MemberExportFormatEnum.CSV) && (
              <Stack direction='row' spacing={1}>
                {format === MemberExportFormatEnum.CSV && (
                  <Button variant='outlined' color='primary' onClick={() => setFormatSelected(false)}>
                    {t('member:exportModal.selectFormatButton')}
                  </Button>
                )}
                <Button variant='contained' color='primary' onClick={handleExport} disableElevation>
                  {t('member:exportModal.exportButton')}
                </Button>
              </Stack>
            )}
          </Box>
        }
      />
    </>
  );
};
